import React from 'react';
import { motion } from 'framer-motion';
import {
  FaCode, FaLaptopCode, FaServer, FaMobileAlt, FaUserGraduate,
  FaRegLightbulb, FaRocket, FaUserAstronaut, FaGlobe, FaTools
} from 'react-icons/fa';
import profile from '../assets/images/profile.jpeg';
import { useInView } from '../hooks/useInView';
import { fadeIn, fadeInUp, staggerContainer } from '../utils/animations';
import { useTranslation } from 'react-i18next';

const About: React.FC = () => {
  const { ref, isInView } = useInView(0.1);
  const { t } = useTranslation();

  const services = [
    {
      title: t('about.services.webDevelopment.title'),
      description: t('about.services.webDevelopment.description'),
      icon: <FaLaptopCode className="text-primary-400" />,
      color: "from-primary-500 to-blue-500"
    },
    {
      title: t('about.services.backendDevelopment.title'),
      description: t('about.services.backendDevelopment.description'),
      icon: <FaServer className="text-accent-400" />,
      color: "from-accent-500 to-teal-500"
    },
    {
      title: t('about.services.mobileDevelopment.title'),
      description: t('about.services.mobileDevelopment.description'),
      icon: <FaMobileAlt className="text-purple-400" />,
      color: "from-purple-500 to-pink-500"
    },
    {
      title: t('about.services.codeArchitecture.title'),
      description: t('about.services.codeArchitecture.description'),
      icon: <FaCode className="text-yellow-400" />,
      color: "from-yellow-500 to-orange-500"
    },
    {
      title: t('about.services.technicalConsultation.title'),
      description: t('about.services.technicalConsultation.description'),
      icon: <FaUserGraduate className="text-red-400" />,
      color: "from-red-500 to-pink-500"
    },
    {
      title: t('about.services.problemSolving.title'),
      description: t('about.services.problemSolving.description'),
      icon: <FaRegLightbulb className="text-teal-400" />,
      color: "from-teal-500 to-green-500"
    }
  ];

  const stats = [
    { value: "4+", label: t('about.stats.experience'), icon: <FaUserAstronaut /> },
    { value: "20+", label: t('about.stats.projects'), icon: <FaRocket /> },
    { value: "15+", label: t('about.stats.clients'), icon: <FaGlobe /> },
    { value: "5+", label: t('about.stats.technologies'), icon: <FaTools /> }
  ];

  return (
    <motion.section
      id="about"
      className="py-24 relative"
      ref={ref as any}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={fadeIn}
    >
      <motion.div 
        className="absolute top-0 right-0 w-1/3 h-1/3 bg-primary-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-5 hidden md:block"
        animate={{
          scale: [1, 1.2, 1],
          opacity: [0.05, 0.08, 0.05]
        }}
        transition={{
          duration: 8,
          repeat: Infinity,
          repeatType: "reverse"
        }}
      ></motion.div>
      <motion.div 
        className="absolute bottom-0 left-0 w-1/3 h-1/3 bg-accent-500 rounded-full mix-blend-multiply filter blur-[100px] opacity-5 hidden md:block"
        animate={{
          scale: [1, 1.3, 1],
          opacity: [0.05, 0.07, 0.05]
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "reverse",
          delay: 1
        }}
      ></motion.div>

      <div className="responsive-container">
        <motion.div 
          className="text-center mb-16"
          variants={fadeInUp}
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
            {t('about.title')}
          </h2>
          <p className="text-gray-400 text-lg max-w-2xl mx-auto">
            {t('about.subtitle')}
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 mb-20">
          <motion.div
            className="lg:col-span-2 flex justify-center items-center"
            variants={fadeInUp}
          >
            <div className="relative">
              <div className="relative w-64 h-64 md:w-80 md:h-80 rounded-2xl overflow-hidden gradient-border p-1">
                <div className="w-full h-full rounded-xl overflow-hidden">
                  <img
                    src={profile}
                    alt="Feres Ben Brahim"
                    className="w-full h-full object-cover"
                    loading="lazy"
                    width="320"
                    height="320"
                    decoding="async"
                    fetchPriority="low"
                  />
                </div>
              </div>

              <motion.div 
                className="absolute -top-4 -right-4 w-20 h-20 glass rounded-lg flex items-center justify-center shadow-glow-sm z-10"
                animate={{ 
                  y: [0, -5, 0],
                  rotate: [0, 5, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  repeatType: "reverse"
                }}
              >
                <FaCode className="text-primary-400 text-2xl" />
              </motion.div>

              <motion.div 
                className="absolute -bottom-4 -left-4 w-20 h-20 glass rounded-lg flex items-center justify-center shadow-glow-sm z-10"
                animate={{ 
                  y: [0, 5, 0],
                  rotate: [0, -5, 0]
                }}
                transition={{
                  duration: 4,
                  repeat: Infinity,
                  repeatType: "reverse",
                  delay: 1
                }}
              >
                <FaServer className="text-accent-400 text-2xl" />
              </motion.div>

              <div className="absolute inset-0 bg-gradient-to-r from-primary-500/20 to-accent-500/20 rounded-2xl -z-10 hidden md:block"></div>
            </div>
          </motion.div>

          <motion.div
            className="lg:col-span-3"
            variants={fadeInUp}
          >
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold mb-4 text-white">{t('about.journey.title')}</h3>
                <p className="text-gray-300 text-lg leading-relaxed">
                  {t('about.journey.description')}
                </p>
              </div>
              <p className="text-gray-300 mb-6 text-lg leading-relaxed">
                {t('about.journey.education')}
              </p>
              <p className="text-gray-300 text-lg leading-relaxed">
                {t('about.journey.personal')}
              </p>

              <motion.div 
                className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8"
                variants={staggerContainer}
              >
                {stats.map((stat, index) => (
                  <motion.div
                    key={index}
                    className="text-center p-4 glass rounded-lg hover:-translate-y-2 hover:scale-105 transition-transform duration-300"
                    variants={fadeInUp}
                    whileHover={{ 
                      boxShadow: "0 0 20px rgba(99, 102, 241, 0.3)",
                      borderColor: "rgba(99, 102, 241, 0.5)"
                    }}
                  >
                    <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gradient-to-r from-primary-500/30 to-accent-500/30 flex items-center justify-center">
                      {stat.icon}
                    </div>
                    <div className="text-2xl font-bold text-white">{stat.value}</div>
                    <div className="text-sm text-gray-400">{stat.label}</div>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="text-center mb-12"
          variants={fadeInUp}
        >
          <h3 className="text-2xl font-semibold mb-3 text-white">{t('about.services.title')}</h3>
          <p className="section-subheading">{t('about.services.subtitle')}</p>
        </motion.div>

        <motion.div
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
          variants={staggerContainer}
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              className="glass rounded-xl p-6 border border-gray-800 hover-lift overflow-hidden relative group"
              variants={fadeInUp}
              whileHover={{ 
                y: -10,
                scale: 1.02,
                transition: { duration: 0.3 }
              }}
            >
              <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 transition-opacity duration-300 -z-10`}></div>
              <div className="w-16 h-16 rounded-lg bg-gradient-to-br from-gray-800 to-gray-700 flex items-center justify-center mb-4 group-hover:shadow-glow-sm transition-all duration-300">
                <div className="text-3xl">{service.icon}</div>
              </div>

              <h4 className="text-xl font-semibold mb-3 text-white group-hover:text-primary-400 transition-colors duration-300">{service.title}</h4>
              <p className="text-gray-300">{service.description}</p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </motion.section>
  );
};

export default About;
